/**
 * コントラスト・明るさ・彩度調整モジュール（Konvaベース + OpenCV contrast）
 * Konva.jsのフィルター機能を使用、contrastのみOpenCVロジック
 */

import Konva from 'konva';

export interface ContrastOptions {
  brightness: number;    // -100 to 100 (0 = no change)
  contrast: number;      // -100 to 100 (0 = no change)
  saturation: number;    // -100 to 100 (0 = no change)
}

export interface CanvasFilterResult {
  blob: Blob;
  width: number;
  height: number;
  processingTime: number;
}

/**
 * Blobから画像オブジェクトを作成（data: URLを使用してCSP制限を回避）
 */
function createImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const img = new Image();
      const dataUrl = reader.result as string;

      img.onload = () => {
        resolve(img);
      };

      img.onerror = () => {
        reject(new Error('Failed to load image from data URL'));
      };

      img.src = dataUrl;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read blob'));
    };

    reader.readAsDataURL(blob);
  });
}

/**
 * OpenCV convertScaleAbsフィルター
 * コントラストと明るさを同時に調整
 * dst = saturate_cast(src * alpha + beta)
 */
Konva.Filters.OpenCVScaleAbs = function(imageData) {
  const data = imageData.data;
  const nPixels = data.length;

  // コントラスト: -100〜100を制限して0.7〜1.3に変換（元の30%に制限）
  // 元の式: alpha = 1.0 + (contrast / 100) → -100で0.0、0で1.0、100で2.0
  // 新しい式: alpha = 1.0 + (contrast / 100) * 0.3 → -100で0.7、0で1.0、100で1.3
  const alpha = 1.0 + ((this.attrs.opencvContrast || 0) / 100) * 0.3;

  // 明るさ: -100〜100をそのまま使用
  const beta = this.attrs.opencvBrightness || 0;

  for (let i = 0; i < nPixels; i += 4) {
    // dst = saturate_cast(src * alpha + beta)
    data[i] = Math.min(255, Math.max(0, data[i] * alpha + beta));     // R
    data[i + 1] = Math.min(255, Math.max(0, data[i + 1] * alpha + beta)); // G
    data[i + 2] = Math.min(255, Math.max(0, data[i + 2] * alpha + beta)); // B
    // data[i + 3] はアルファチャンネルなので変更しない
  }
};

/**
 * コントラスト・明るさ・彩度調整（Konvaベース）
 */
export async function adjustContrast(
  imageBlob: Blob,
  options: ContrastOptions
): Promise<CanvasFilterResult> {
  const startTime = performance.now();

  try {
    // 何も調整しない場合は早期リターン
    if (options.brightness === 0 && options.contrast === 0 && options.saturation === 0) {
      const img = await createImageFromBlob(imageBlob);
      return {
        blob: imageBlob,
        width: img.width,
        height: img.height,
        processingTime: performance.now() - startTime
      };
    }

    // 画像を読み込み
    const image = await createImageFromBlob(imageBlob);

    // 仮想コンテナを作成（実際にDOMに追加しない）
    const container = document.createElement('div');
    container.style.display = 'none';
    document.body.appendChild(container);

    // Konvaステージを作成
    const stage = new Konva.Stage({
      container: container,
      width: image.width,
      height: image.height
    });

    const layer = new Konva.Layer();

    // フィルターを適用したKonva画像を作成
    const filters: Konva.Filter[] = [];

    // OpenCV convertScaleAbsフィルター（明るさとコントラストを同時に処理）
    if (options.brightness !== 0 || options.contrast !== 0) {
      filters.push(Konva.Filters.OpenCVScaleAbs);
    }

    // 彩度フィルター（HSLフィルターを使用）
    if (options.saturation !== 0) {
      filters.push(Konva.Filters.HSL);
    }

    const konvaImage = new Konva.Image({
      x: 0,
      y: 0,
      image: image,
      filters: filters.length > 0 ? filters : undefined
    });

    // フィルターパラメータを設定
    if (options.brightness !== 0 || options.contrast !== 0) {
      // OpenCV convertScaleAbsパラメータ設定
      konvaImage.setAttr('opencvContrast', options.contrast);
      konvaImage.setAttr('opencvBrightness', options.brightness);
    }

    if (options.saturation !== 0) {
      // HSLフィルターの彩度調整（-1〜1の範囲）
      konvaImage.saturation(options.saturation / 100);
    }

    // レイヤーに追加
    layer.add(konvaImage);
    stage.add(layer);

    // キャッシュを有効化（フィルターを適用するために必須）
    // 注意: cache()を使用するとデバイスピクセル比の影響で画像サイズが変わるが、
    // 後でリサイズ処理で元のサイズに戻す
    if (filters.length > 0) {
      konvaImage.cache();
    }

    // レイヤーを描画
    layer.draw();

    // Canvasを使用してBlobを直接取得
    return new Promise((resolve, reject) => {
      // 少し待ってから処理（描画完了を確実にする）
      setTimeout(() => {
        try {
          // Konvaのcanvas要素から直接Blobを取得
          const sourceCanvas = layer.getCanvas()._canvas;

          // Konvaが自動的にデバイスピクセル比でスケールするため、
          // 元のサイズに戻す必要がある
          if (sourceCanvas.width !== image.width || sourceCanvas.height !== image.height) {
            // 新しいcanvasを作成して元のサイズにリサイズ
            const resizedCanvas = document.createElement('canvas');
            resizedCanvas.width = image.width;
            resizedCanvas.height = image.height;
            const ctx = resizedCanvas.getContext('2d');

            if (ctx) {
              // 高品質設定でリサイズ
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = 'high';
              ctx.drawImage(sourceCanvas, 0, 0, image.width, image.height);

              resizedCanvas.toBlob((blob) => {
                // クリーンアップ
                stage.destroy();
                document.body.removeChild(container);

                if (blob) {
                  resolve({
                    blob: blob,
                    width: image.width,
                    height: image.height,
                    processingTime: performance.now() - startTime
                  });
                } else {
                  reject(new Error('Failed to create blob from canvas'));
                }
              }, 'image/jpeg', 1.0);
            } else {
              reject(new Error('Failed to get canvas context'));
            }
          } else {
            // サイズが一致している場合はそのまま使用
            sourceCanvas.toBlob((blob) => {
              // クリーンアップ
              stage.destroy();
              document.body.removeChild(container);

              if (blob) {
                resolve({
                  blob: blob,
                  width: image.width,
                  height: image.height,
                  processingTime: performance.now() - startTime
                });
              } else {
                reject(new Error('Failed to create blob from canvas'));
              }
            }, 'image/jpeg', 1.0);
          }
        } catch (error) {
          stage.destroy();
          document.body.removeChild(container);
          reject(error);
        }
      }, 0);
    });

  } catch (error) {
    console.error('Contrast adjustment failed:', error);
    throw error;
  }
}